/**
 * SettingsPage Component
 * AIDLC Administrative Backend
 *
 * Module 08: Settings
 * System configuration display and management.
 *
 * Features:
 * - Azure resource configuration display
 * - Authentication status
 * - API endpoint health check
 * - System information
 * - Agent registration status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './Card';
import { Badge } from './Badge';
import { Button } from './Button';
import { SmsConsentModal } from './SmsConsentModal';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';
import { useAgents } from '../../hooks/useAgents';
import { useSmsConsent } from '../../hooks/useSmsConsent';
import apiClient from '../../services/api';
import { settingsSaveError } from '../../utils/settingsSaveError';
import {
  getNotificationChannelConfig,
  updateNotificationChannelConfig,
  getNotificationChannelStatus,
  getNotificationPreferences,
  updateNotificationPreferences,
  generateVapidKeys as apiGenerateVapidKeys,
  sendTestNotification,
  type NotificationChannelConfig,
  type ChannelStatus,
  type NotificationPreferences,
} from '../../services/notificationService';

interface HealthStatus {
  status: string;
  version: string;
  timestamp: string;
  uptime: number;
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

function StatusDot({ active, label }: { active: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} />
      <span className="text-xs text-gray-700">{label}</span>
      <span className={`text-[10px] ${active ? 'text-green-600' : 'text-gray-400'}`}>
        {active ? 'Configured' : 'Not configured'}
      </span>
    </div>
  );
}

export function SettingsPage() {
  const { user, isAuthenticated, isAdmin } = useAuth();
  const { agents } = useAgents(0);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  // Notification channel state
  const [channelConfig, setChannelConfig] = useState<NotificationChannelConfig | null>(null);
  const [channelStatus, setChannelStatus] = useState<ChannelStatus>({ email: false, sms: false, push: false });
  const [prefs, setPrefs] = useState<NotificationPreferences>({ email: true, sms: false, push: true, emailAddress: null, phoneNumber: null });
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifFeedback, setNotifFeedback] = useState<string | null>(null);
  // Failed saves, in plain words by status (SETTINGS-ERR-1); each shows beside its own Save button until the next try
  const [channelsSaveError, setChannelsSaveError] = useState<string | null>(null);
  const [prefsSaveError, setPrefsSaveError] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; msg: string } | null>>({});
  const [testLoading, setTestLoading] = useState<Record<string, boolean>>({});

  // SMS consent gating — fields stay disabled until the user consents
  const {
    hasConsented: smsConsented,
    consentedPhoneNumber,
    isModalOpen: smsConsentModalOpen,
    openModal: openSmsConsentModal,
    closeModal: closeSmsConsentModal,
    grantConsent: grantSmsConsent,
  } = useSmsConsent();

  // When user grants consent, populate the ACS phone number field
  const handleSmsConsent = useCallback((phoneNumber: string) => {
    grantSmsConsent(phoneNumber);
    // Pre-fill the channel config phone field with the consented number
    setChannelConfig(prev => prev ? { ...prev, acsPhoneNumber: prev.acsPhoneNumber || phoneNumber } : prev);
    // Also update prefs phone number
    setPrefs(prev => ({ ...prev, phoneNumber: prev.phoneNumber || phoneNumber }));
  }, [grantSmsConsent]);

  const checkHealth = async () => {
    setIsCheckingHealth(true);
    setHealthError(null);
    try {
      const response = await apiClient.get<HealthStatus>('/health');
      setHealth(response.data);
    } catch (err) {
      setHealthError(err instanceof Error ? err.message : 'Health check failed');
    } finally {
      setIsCheckingHealth(false);
    }
  };

  const loadNotifData = useCallback(async () => {
    try {
      const [config, status, prefData] = await Promise.all([
        getNotificationChannelConfig(),
        getNotificationChannelStatus(),
        getNotificationPreferences(),
      ]);
      setChannelConfig(config);
      setChannelStatus(status);
      setPrefs(prefData);
    } catch {
      // Settings endpoints may not be available yet
    }
  }, []);

  useEffect(() => {
    checkHealth();
    loadNotifData();
  }, [loadNotifData]);

  const handleNotifSave = async () => {
    if (!channelConfig) return;
    setNotifSaving(true);
    setNotifFeedback(null);
    setChannelsSaveError(null);
    try {
      const result = await updateNotificationChannelConfig(channelConfig);
      setChannelStatus(result.channelStatus);
      setNotifFeedback('Configuration saved');
      setTimeout(() => setNotifFeedback(null), 3000);
    } catch (err) {
      console.error('[Settings] Saving the notification channel settings failed:', err);
      setChannelsSaveError(settingsSaveError('channels', err));
    } finally {
      setNotifSaving(false);
    }
  };

  const handlePrefsSave = async () => {
    setNotifSaving(true);
    setPrefsSaveError(null);
    try {
      const updated = await updateNotificationPreferences(prefs);
      setPrefs(updated);
      setNotifFeedback('Preferences saved');
      setTimeout(() => setNotifFeedback(null), 3000);
    } catch (err) {
      console.error('[Settings] Saving the default notification preferences failed:', err);
      setPrefsSaveError(settingsSaveError('preferences', err));
    } finally {
      setNotifSaving(false);
    }
  };

  const handleGenerateVapid = async () => {
    try {
      const keys = await apiGenerateVapidKeys();
      setChannelConfig(prev => prev ? { ...prev, vapidPublicKey: keys.publicKey, vapidPrivateKey: keys.privateKey } : prev);
      setNotifFeedback('VAPID keys generated — save to apply');
    } catch {
      setNotifFeedback('Error generating VAPID keys');
    }
  };

  const handleTest = async (channel: 'email' | 'sms' | 'push') => {
    setTestLoading(prev => ({ ...prev, [channel]: true }));
    setTestResults(prev => ({ ...prev, [channel]: null }));
    try {
      const result = await sendTestNotification(channel);
      setTestResults(prev => ({ ...prev, [channel]: { ok: result.ok, msg: result.message || 'Sent' } }));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed';
      setTestResults(prev => ({ ...prev, [channel]: { ok: false, msg } }));
    } finally {
      setTestLoading(prev => ({ ...prev, [channel]: false }));
    }
  };

  const formatUptime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  return (
    <div className="space-y-6">
      {/* System Status */}
      <Card title="System Status" subtitle="Backend API health and uptime">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                health?.status === 'healthy' ? 'bg-green-500' :
                healthError ? 'bg-red-500' : 'bg-gray-400'
              }`} />
              <div>
                <p className="text-sm font-medium text-ldaja-black">
                  API Server
                </p>
                <p className="text-xs text-gray-500">
                  {health?.status === 'healthy' ? 'Healthy and running' :
                   healthError ? `Error: ${healthError}` : 'Checking...'}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={checkHealth} isLoading={isCheckingHealth}>
              Check Health
            </Button>
          </div>

          {health && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-[10px] text-gray-400">Version</p>
                <p className="text-xs font-medium text-gray-700 font-mono">{health.version}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Uptime</p>
                <p className="text-xs font-medium text-gray-700">{formatUptime(health.uptime)}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Last Check</p>
                <p className="text-xs font-medium text-gray-700">
                  {new Date(health.timestamp).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400">Status</p>
                <Badge variant="success" dot>{health.status}</Badge>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Authentication */}
      <Card title="Authentication" subtitle="Microsoft Entra ID (Azure AD) configuration">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] text-gray-400">Status</p>
              <Badge variant={isAuthenticated ? 'success' : 'danger'} dot>
                {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
              </Badge>
            </div>
            <div>
              <p className="text-[10px] text-gray-400">Role</p>
              <Badge variant={isAdmin ? 'info' : 'default'}>
                {user?.roles[0] || 'No Role'}
              </Badge>
            </div>
            <div>
              <p className="text-[10px] text-gray-400">User</p>
              <p className="text-xs font-medium text-gray-700">{user?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400">Email</p>
              <p className="text-xs font-medium text-gray-700">{user?.email || 'N/A'}</p>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Auth Configuration</h4>
            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Auth Method</span>
                <span className="text-gray-700 font-mono">MSAL.js 2.0 (PKCE)</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Identity Provider</span>
                <span className="text-gray-700 font-mono">Microsoft Entra ID</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Cache Location</span>
                <span className="text-gray-700 font-mono">sessionStorage</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">RBAC Roles</span>
                <span className="text-gray-700 font-mono">Admin, Operator, Viewer</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Notification Channels */}
      <Card title="Notification Channels" subtitle="Configure email, SMS, and push delivery">
        <div className="space-y-6">
          {/* Channel Status Banner */}
          <div className="flex items-center gap-6 p-3 bg-gray-50 rounded-lg">
            <StatusDot active={channelStatus.email} label="Email" />
            <StatusDot active={channelStatus.sms} label="SMS" />
            <StatusDot active={channelStatus.push} label="Push" />
          </div>

          {channelConfig && (
            <>
              {/* Email: one fixed sender, nothing to configure (KD-1 E4) */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700">Email</h4>
                <p className="text-xs text-gray-600">Sends as agents@ldaja.com</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleTest('email')}
                    isLoading={testLoading.email}
                  >
                    Send Test Email
                  </Button>
                  {testResults.email && (
                    <span className={`text-[10px] ${testResults.email.ok ? 'text-green-600' : 'text-red-500'}`}>
                      {testResults.email.msg}
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* SMS (Azure Communication Services) — Gated by consent */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <h4 className="text-xs font-semibold text-gray-700">SMS (Azure Communication Services)</h4>
                  <button
                    onClick={openSmsConsentModal}
                    className="text-[11px] font-medium text-ldaja-cyan hover:text-ldaja-blue transition-colors underline underline-offset-2 decoration-ldaja-cyan/40 hover:decoration-ldaja-blue/60"
                  >
                    SMS Consent
                  </button>
                  {smsConsented && (
                    <span className="text-[10px] text-green-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      Consented
                    </span>
                  )}
                </div>

                {/* Consent gate: show message when not consented, fields when consented */}
                {!smsConsented ? (
                  <div className="bg-amber-50/70 border border-amber-200 rounded-lg px-4 py-3">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      You must provide SMS consent before configuring this feature.{' '}
                      <button
                        onClick={openSmsConsentModal}
                        className="font-semibold text-ldaja-blue hover:text-ldaja-blue-dark underline underline-offset-2 transition-colors"
                      >
                        Open SMS Consent
                      </button>
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormField label="Connection String">
                        <input
                          type="password"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                          value={channelConfig.acsConnectionString}
                          onChange={e => setChannelConfig({ ...channelConfig, acsConnectionString: e.target.value })}
                          placeholder="endpoint=https://..."
                        />
                      </FormField>
                      <FormField label="Phone Number">
                        <input
                          type="tel"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                          value={channelConfig.acsPhoneNumber}
                          onChange={e => setChannelConfig({ ...channelConfig, acsPhoneNumber: e.target.value })}
                          placeholder="+18555551234"
                        />
                      </FormField>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleTest('sms')}
                        isLoading={testLoading.sms}
                      >
                        Send Test SMS
                      </Button>
                      {testResults.sms && (
                        <span className={`text-[10px] ${testResults.sms.ok ? 'text-green-600' : 'text-red-500'}`}>
                          {testResults.sms.msg}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* SMS Consent Modal */}
              <SmsConsentModal
                isOpen={smsConsentModalOpen}
                onConsent={handleSmsConsent}
                onDecline={closeSmsConsentModal}
                initialPhoneNumber={consentedPhoneNumber}
              />

              <div className="border-t border-gray-100" />

              {/* Push (Web Push / VAPID) */}
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700">Push (Web Push / VAPID)</h4>
                <div className="grid grid-cols-1 gap-3">
                  <FormField label="VAPID Public Key">
                    <input
                      type="text"
                      readOnly
                      className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 font-mono text-gray-500"
                      value={channelConfig.vapidPublicKey}
                    />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormField label="VAPID Private Key">
                      <input
                        type="password"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                        value={channelConfig.vapidPrivateKey}
                        onChange={e => setChannelConfig({ ...channelConfig, vapidPrivateKey: e.target.value })}
                        placeholder="VAPID private key"
                      />
                    </FormField>
                    <FormField label="Subject URI">
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                        value={channelConfig.vapidSubject}
                        onChange={e => setChannelConfig({ ...channelConfig, vapidSubject: e.target.value })}
                        placeholder="mailto:neo@ldaja.ai"
                      />
                    </FormField>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={handleGenerateVapid}>
                    Generate VAPID Keys
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleTest('push')}
                    isLoading={testLoading.push}
                  >
                    Test Push
                  </Button>
                  {testResults.push && (
                    <span className={`text-[10px] ${testResults.push.ok ? 'text-green-600' : 'text-red-500'}`}>
                      {testResults.push.msg}
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100" />

              {/* Save Config */}
              <div className="flex items-center gap-3 flex-wrap">
                <Button size="sm" onClick={handleNotifSave} isLoading={notifSaving}>
                  Save Channel Config
                </Button>
                {notifFeedback && (
                  <span className={`text-xs ${notifFeedback.startsWith('Error') ? 'text-red-500' : 'text-green-600'}`}>
                    {notifFeedback}
                  </span>
                )}
              </div>
              {channelsSaveError && (
                <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                  {channelsSaveError}
                </p>
              )}
            </>
          )}

          <div className="border-t border-gray-100" />

          {/* Default Preferences */}
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Default Notification Preferences</h4>
            <div className="flex items-center gap-6">
              {(['email', 'sms', 'push'] as const).map(ch => (
                <label key={ch} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={prefs[ch]}
                    onChange={e => setPrefs({ ...prefs, [ch]: e.target.checked })}
                  />
                  {ch.charAt(0).toUpperCase() + ch.slice(1)}
                </label>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Default Email Address">
                <input
                  type="email"
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                  value={prefs.emailAddress || ''}
                  onChange={e => setPrefs({ ...prefs, emailAddress: e.target.value || null })}
                  placeholder="ceo@ldaja.ai"
                />
              </FormField>
              <FormField label="Default Phone Number">
                <input
                  type="tel"
                  className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 font-mono"
                  value={prefs.phoneNumber || ''}
                  onChange={e => setPrefs({ ...prefs, phoneNumber: e.target.value || null })}
                  placeholder="+15551234567"
                />
              </FormField>
            </div>
            <Button size="sm" variant="ghost" onClick={handlePrefsSave} isLoading={notifSaving}>
              Save Preferences
            </Button>
            {prefsSaveError && (
              <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                {prefsSaveError}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Registered Agents */}
      <Card
        title="Registered Agents"
        subtitle={`${agents.length} agents in the system`}
      >
        <div className="space-y-2">
          {agents.length === 0 ? (
            <p className="text-xs text-gray-400">No agents registered.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {agents.map((agent) => (
                <div key={agent.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: agent.avatarColor || '#2563EB' }}
                  >
                    {agent.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{agent.name}</p>
                    <p className="text-[10px] text-gray-500 truncate">{agent.role}</p>
                  </div>
                  <Badge
                    variant={
                      agent.status === 'working' ? 'success' :
                      agent.status === 'available' ? 'info' :
                      agent.status === 'waiting' ? 'warning' : 'neutral'
                    }
                    dot
                  >
                    {agent.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Azure Infrastructure */}
      <Card title="Azure Infrastructure" subtitle="Todd's provisioned resources">
        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Resource Group</span>
            <span className="text-gray-700 font-mono">rg-aidlc-prod</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Cosmos DB Account</span>
            <span className="text-gray-700 font-mono">cosmos-aidlc-prod</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">App Service</span>
            <span className="text-gray-700 font-mono">app-aidlc-admin</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Static Web App</span>
            <span className="text-gray-700 font-mono">swa-aidlc-frontend</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Key Vault</span>
            <span className="text-gray-700 font-mono">kv-aidlc-prod</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Application Insights</span>
            <span className="text-gray-700 font-mono">ai-aidlc-prod</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Region</span>
            <span className="text-gray-700 font-mono">East US 2</span>
          </div>
        </div>
      </Card>

      {/* Database Schema */}
      <Card title="Database Containers" subtitle="Raj's Cosmos DB schema (8 containers)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { name: 'agents', partition: '/id', icon: 'grid' },
            { name: 'projects', partition: '/id', icon: 'folder' },
            { name: 'sessions', partition: '/projectId', icon: 'clock' },
            { name: 'audit-logs', partition: '/agentId', icon: 'list' },
            { name: 'prompt-queue', partition: '/toAgent', icon: 'mail' },
            { name: 'chat-messages', partition: '/conversationId', icon: 'message' },
            { name: 'task-templates', partition: '/category', icon: 'file' },
            { name: 'governance', partition: '/type', icon: 'shield' },
          ].map((container) => (
            <div key={container.name} className="bg-gray-50 rounded p-2">
              <p className="text-xs font-medium text-gray-700">{container.name}</p>
              <p className="text-[10px] text-gray-400 font-mono">PK: {container.partition}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* API Endpoints */}
      <Card title="API Endpoints" subtitle="Backend REST API routes">
        <div className="space-y-1 font-mono text-xs">
          {[
            { method: 'GET', path: '/api/health', description: 'Health check' },
            { method: 'GET', path: '/api/agents', description: 'List all agents' },
            { method: 'GET', path: '/api/agents/:id', description: 'Get agent by ID' },
            { method: 'PUT', path: '/api/agents/:id/status', description: 'Update agent status' },
            { method: 'PUT', path: '/api/agents/:id/workspace', description: 'Update workspace' },
            { method: 'GET', path: '/api/projects', description: 'List projects' },
            { method: 'POST', path: '/api/projects', description: 'Create project' },
            { method: 'PUT', path: '/api/projects/:id/state', description: 'Transition state' },
            { method: 'GET', path: '/api/chat/conversations', description: 'List conversations' },
            { method: 'POST', path: '/api/chat/conversations/:id/messages', description: 'Send message' },
            { method: 'POST', path: '/api/chat/conversations/:id/execute', description: 'Go Do It' },
            { method: 'GET', path: '/api/prompts', description: 'List prompt queue' },
            { method: 'GET', path: '/api/audit', description: 'Query audit logs' },
            { method: 'GET', path: '/api/governance/rules', description: 'List governance rules' },
            { method: 'GET', path: '/api/templates', description: 'List templates' },
            { method: 'GET', path: '/api/sessions', description: 'List sessions' },
          ].map((endpoint, idx) => (
            <div key={idx} className="flex items-center gap-3 py-1 border-b border-gray-50 last:border-0">
              <span className={`w-12 text-center text-[10px] font-bold rounded px-1 py-0.5 ${
                endpoint.method === 'GET' ? 'bg-green-100 text-green-700' :
                endpoint.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {endpoint.method}
              </span>
              <span className="text-gray-700 flex-1">{endpoint.path}</span>
              <span className="text-gray-400 text-[10px] font-sans">{endpoint.description}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Version Info */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          AIDLC Administrative Backend v0.1.0 -- LDAJA Operations
        </p>
        <p className="text-[10px] text-gray-300 mt-0.5">
          Built by the AIDLC agent team
        </p>
      </div>
    </div>
  );
}
